// Format seconds as MM:SS
export function formatElapsed(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

// Format seconds as HH:MM:SS
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

// Format pace as MM:SS per mile
export function formatPace(paceMinPerMile: number): string {
  const mins = Math.floor(paceMinPerMile)
  const secs = Math.round((paceMinPerMile - mins) * 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const DEFAULT_TZ = 'America/Los_Angeles'

export function formatTime(iso: string | null, tz = DEFAULT_TZ): string {
  if (!iso) return '-'
  return new Date(iso).toLocaleTimeString('en-US', { timeZone: tz, hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

export function formatDateTime(iso: string | null, tz = DEFAULT_TZ): string {
  if (!iso) return '-'
  return new Date(iso).toLocaleString('en-US', { timeZone: tz, month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export function formatTimePT(date: Date, tz = DEFAULT_TZ): string {
  return date.toLocaleTimeString('en-US', { timeZone: tz, hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

// Convert an ISO string to a datetime-local input value (YYYY-MM-DDTHH:MM) in the given timezone
export function isoToLocalInput(iso: string | null | undefined, tz = DEFAULT_TZ): string {
  if (!iso) return ''
  const d = new Date(iso)
  // Format each part in the target timezone
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d)
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00'
  const hour = get('hour') === '24' ? '00' : get('hour') // midnight edge case
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`
}

// Convert a datetime-local input value (YYYY-MM-DDTHH:MM) in the given timezone to ISO string
export function localInputToISO(val: string, tz = DEFAULT_TZ): string | null {
  if (!val) return null
  // Parse the local date parts from the input string
  const [datePart, timePart] = val.split('T')
  if (!datePart || !timePart) return null
  const [year, month, day] = datePart.split('-').map(Number)
  const [hour, minute] = timePart.split(':').map(Number)
  // Use Intl to find the UTC offset for this moment in the target timezone
  // We construct a UTC date and adjust by the offset
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute)
  const localStr = new Date(utcGuess).toLocaleString('en-US', { timeZone: tz, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
  // localStr is the TZ-local representation of utcGuess; compute the drift
  const [mDate, mTime] = localStr.split(', ')
  const [mMonth, mDay, mYear] = mDate.split('/').map(Number)
  const [mHour, mMinute] = mTime.split(':').map(Number)
  const localAsUtc = Date.UTC(mYear, mMonth - 1, mDay, mHour === 24 ? 0 : mHour, mMinute)
  const offsetMs = utcGuess - localAsUtc
  return new Date(utcGuess + offsetMs).toISOString()
}
